import { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RiSearchLine, RiMore2Line } from "react-icons/ri";
import Dropdown from "@/Utility/Dropdown/Dropdown";
import Input from "@/Utility/Input/Input";
import Checkbox from "@/Utility/Checkbox/Checkbox";
import Typography from "@/Utility/Typography";
import FilterToggle from "@/Utility/FilterToggle/FilterToggle";
import Loader from "@/Utility/Loader/Loader";
import ListPaginationBar from "@/Utility/Pagination/ListPaginationBar";
import { BACKLOG_LIST_PAGE_SIZE_STORAGE_KEY } from "@/Utility/Pagination/listPaginationConstants";
import { useListPageSize } from "@/hooks/useListPageSize";
import { selectBacklogData, selectBacklogLoading } from "@/Redux/Slices/backlogSlice";
import { fetchBacklogData } from "@/Redux/Thunks/backlogThunk";
import { fetchUsersData } from "@/Redux/Thunks/usersThunk";
import { fetchSystemList } from "@/Redux/Thunks/systemListsThunk";
import { fetchProjectStatuses } from "@/Redux/Thunks/projectStatusesThunk";
import { selectUsersData } from "@/Redux/Slices/usersSlice";
import { selectProjectStatusesResponse } from "@/Redux/Slices/projectStatusesSlice";
import {
  getListFromApiResponse,
  mapUserToAssigneeOptionForKanban,
  mapSystemListItemToOption,
} from "@/Pages/Issues/issueMappers";
import { formatDate } from "@/utils/dateUtils";
import toast from "react-hot-toast";

const colorPalette = [
  "#2563EB",
  "#7C3AED",
  "#0F766E",
  "#EA580C",
  "#1E293B",
  "#DB2777",
  "#059669",
  "#9333EA",
];

const getColorByIndex = (index) => colorPalette[index % colorPalette.length];

const UNASSIGNED_VALUE = "__unassigned__";

/**
 * @param {string | undefined} projectSlug — When set (project boards), backlog is scoped; otherwise global (/issues).
 * @param {(issue: object) => void} [onRequestEditWorkItem] — Opens backlog edit (PATCH), same as project Backlog tab.
 */
const ProjectBacklog = ({ projectSlug, onRequestEditWorkItem }) => {
  const dispatch = useDispatch();
  const backlogData = useSelector(selectBacklogData);
  const loading = useSelector(selectBacklogLoading);
  const usersData = useSelector(selectUsersData);
  const statusesResponse = useSelector((state) =>
    projectSlug ? selectProjectStatusesResponse(state, projectSlug) : null
  );

  const issues = useMemo(() => backlogData?.list ?? [], [backlogData]);
  const meta = backlogData?.meta;

  const [isOpen, setIsOpen] = useState(false);
  const { pageSize, setPageSize } = useListPageSize(BACKLOG_LIST_PAGE_SIZE_STORAGE_KEY);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [assignee, setAssignee] = useState("");
  const [priority, setPriority] = useState("");
  const [workItemType, setWorkItemType] = useState("");
  const [status, setStatus] = useState("");
  const [typeFilterOptions, setTypeFilterOptions] = useState([{ label: "All", value: "" }]);
  const [priorityFilterOptions, setPriorityFilterOptions] = useState([{ label: "All", value: "" }]);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const toggleSelect = (id, isChecked) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isChecked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  useEffect(() => {
    dispatch(fetchUsersData({ getAll: true }));
  }, [dispatch]);

  useEffect(() => {
    if (projectSlug) {
      dispatch(fetchProjectStatuses(projectSlug));
    }
  }, [dispatch, projectSlug]);

  useEffect(() => {
    dispatch(fetchSystemList({ listType: "WORK_ITEM_TYPE", context: "project-backlog-type" }))
      .unwrap()
      .then((raw) => {
        const list = getListFromApiResponse(raw);
        const opts = [
          { label: "All", value: "" },
          ...list.map((item) => mapSystemListItemToOption(item)),
        ];
        setTypeFilterOptions(opts);
      })
      .catch(() => setTypeFilterOptions([{ label: "All", value: "" }]));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSystemList({ listType: "PRIORITY", context: "project-backlog-priority" }))
      .unwrap()
      .then((raw) => {
        const list = getListFromApiResponse(raw);
        const opts = [
          { label: "All", value: "" },
          ...list.map((item) => mapSystemListItemToOption(item)),
        ];
        setPriorityFilterOptions(opts);
      })
      .catch(() => setPriorityFilterOptions([{ label: "All", value: "" }]));
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
    setWorkItemType("");
    setStatus("");
  }, [projectSlug]);

  useEffect(() => {
    const params = projectSlug
      ? { projectId: projectSlug, page: currentPage, limit: pageSize }
      : { page: currentPage, limit: pageSize };
    dispatch(fetchBacklogData(params));
  }, [dispatch, projectSlug, currentPage, pageSize]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const assigneeOptions = useMemo(() => {
    const users = getListFromApiResponse(usersData);
    const mapped = users.map((user, index) =>
      mapUserToAssigneeOptionForKanban(user, index, getColorByIndex)
    );
    return [
      { label: "All", value: "" },
      { label: "Unassigned", value: UNASSIGNED_VALUE },
      ...mapped.map((o) => ({
        label: o.name,
        value: o.id,
        showAvatar: true,
      })),
    ];
  }, [usersData]);

  const priorityOptionsForDropdown = useMemo(() => {
    if (priorityFilterOptions.length > 0) return priorityFilterOptions;
    return [{ label: "All", value: "" }];
  }, [priorityFilterOptions]);

  const typeOptionsForDropdown = useMemo(() => {
    if (typeFilterOptions.length > 0) return typeFilterOptions;
    return [{ label: "All", value: "" }];
  }, [typeFilterOptions]);

  /** Same source as project Work Items tab — project workflow statuses. */
  const statusOptionsForDropdown = useMemo(() => {
    const base = [{ label: "All", value: "" }];
    if (!projectSlug || !statusesResponse?.data?.length) return base;
    const fromApi = statusesResponse.data
      .slice()
      .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0))
      .map((s) => ({ label: s.name, value: String(s.id) }));
    return [...base, ...fromApi];
  }, [projectSlug, statusesResponse]);

  const filteredIssues = useMemo(() => {
    const q = search.trim().toLowerCase();
    return issues.filter((issue) => {
      const matchSearch =
        !q ||
        String(issue.issueKey ?? "")
          .toLowerCase()
          .includes(q) ||
        String(issue.title ?? "")
          .toLowerCase()
          .includes(q);
      const matchAssignee =
        !assignee ||
        (assignee === UNASSIGNED_VALUE
          ? !(issue.assigneeId != null && String(issue.assigneeId).trim() !== "")
          : String(issue.assigneeId ?? "") === String(assignee));
      const matchPriority =
        !priority ||
        String(issue.priorityId ?? "") === String(priority) ||
        (!issue.priorityId && issue.priority && String(issue.priority) === priority);
      const matchType = !workItemType || String(issue.typeId ?? "") === String(workItemType);
      const matchStatus = !status || String(issue.statusId ?? "") === String(status);
      return matchSearch && matchAssignee && matchPriority && matchType && matchStatus;
    });
  }, [issues, search, assignee, priority, workItemType, status]);

  const rawTotal = meta?.total ?? meta?.totalCount;
  const totalCount =
    rawTotal != null && String(rawTotal).trim() !== "" && Number.isFinite(Number(rawTotal))
      ? Number(rawTotal)
      : issues.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const openEditFromRow = (issue) => {
    const workItemId = issue?.id ?? issue?.workItemId;
    if (workItemId == null || String(workItemId).trim() === "") {
      toast.error("Cannot open this row: missing work item id.");
      return;
    }
    onRequestEditWorkItem?.(issue);
  };

  return (
    <div className="bg-white border rounded-lg">
      <FilterToggle isExpanded={isOpen} label="Backlog" onToggle={() => setIsOpen((p) => !p)} />

      {isOpen && (
        <div className="p-4">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative w-full lg:w-[calc(25%-0.5rem)] md:w-[calc(50%-0.5rem)] min-w-[200px]">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search backlog..."
                noWrapper
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm rounded-md"
              />
            </div>
            <div className="w-full lg:w-[calc(25%-0.5rem)] md:w-[calc(50%-0.5rem)] min-w-[180px]">
              <Dropdown
                name="assignee"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                options={assigneeOptions}
                placeholder="Assignee: All"
              />
            </div>
            <div className="w-full lg:w-[calc(25%-0.5rem)] md:w-[calc(50%-0.5rem)] min-w-[180px]">
              <Dropdown
                name="priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                options={priorityOptionsForDropdown}
                placeholder="Priority: All"
              />
            </div>
            <div className="w-full lg:w-[calc(25%-0.5rem)] md:w-[calc(50%-0.5rem)] min-w-[180px]">
              <Dropdown
                name="workItemType"
                value={workItemType}
                onChange={(e) => setWorkItemType(e.target.value)}
                options={typeOptionsForDropdown}
                placeholder="Type: All"
              />
            </div>
            {projectSlug && statusOptionsForDropdown.length > 1 ? (
              <div className="w-full lg:w-[calc(25%-0.5rem)] md:w-[calc(50%-0.5rem)] min-w-[180px]">
                <Dropdown
                  name="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  options={statusOptionsForDropdown}
                  placeholder="Status: All"
                />
              </div>
            ) : null}
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-[40px_1.5fr_1fr_1fr_1fr_1fr_40px] text-xs text-gray-500 border-b py-2">
                <div />
                <div>Issue</div>
                <div>Assignee</div>
                <div>Priority</div>
                <div>Due Date</div>
                <div>Status</div>
                <div />
              </div>

              {filteredIssues.length === 0 ? (
                <Typography variant="text2" className="text-text-secondary py-6 text-center block">
                  No backlog items
                </Typography>
              ) : (
                filteredIssues.map((issue) => {
                  const rowKey = issue.id != null ? String(issue.id) : issue.issueKey;
                  const due =
                    issue.dueDate != null && String(issue.dueDate).trim() !== ""
                      ? formatDate(issue.dueDate)
                      : "-";
                  const canEditHere = typeof onRequestEditWorkItem === "function";
                  return (
                    <div
                      key={rowKey}
                      role={canEditHere ? "button" : undefined}
                      tabIndex={canEditHere ? 0 : undefined}
                      aria-label={
                        canEditHere ? `Edit work item ${issue.issueKey || rowKey}` : undefined
                      }
                      className={`grid grid-cols-[40px_1.5fr_1fr_1fr_1fr_1fr_40px] items-center text-sm border-b py-3 hover:bg-gray-50 ${
                        canEditHere
                          ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          : ""
                      }`}
                      onClick={() => canEditHere && openEditFromRow(issue)}
                      onKeyDown={(e) => {
                        if (!canEditHere) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openEditFromRow(issue);
                        }
                      }}
                    >
                      <div
                        className="flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          name={`select-${rowKey}`}
                          checked={selectedIds.has(rowKey)}
                          onChange={(e) => toggleSelect(rowKey, e.target.checked)}
                          noWrapper
                        />
                      </div>

                      <div className="font-medium min-w-0">
                        <Typography variant="span2" className="text-gray-500 mr-2">
                          {issue.issueKey || "—"}
                        </Typography>
                        <span className="break-words">{issue.title || "—"}</span>
                      </div>

                      <div className="min-w-0 truncate">{issue.assignee || "Unassigned"}</div>

                      <div>
                        <Typography
                          variant="caption"
                          className="px-2 py-1 rounded-md bg-orange-100 text-orange-700 inline-block"
                        >
                          {issue.priority || "—"}
                        </Typography>
                      </div>

                      <div>{due}</div>

                      <div>
                        <Typography
                          variant="caption"
                          className="px-2 py-1 rounded-md bg-blue-100 text-blue-700 inline-block"
                        >
                          {issue.status || "—"}
                        </Typography>
                      </div>

                      <RiMore2Line
                        className="text-gray-400 shrink-0 pointer-events-none"
                        aria-hidden
                      />
                    </div>
                  );
                })
              )}

              <ListPaginationBar
                className="mt-4"
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectBacklog;
